import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { auth } from '../firebase';

const AuthContext = createContext(null);

function firebaseUserToAppUser(fbUser) {
  if (!fbUser) return null;
  return {
    uid: fbUser.uid,
    email: fbUser.email || '',
    name: fbUser.displayName || fbUser.email?.split('@')[0] || 'User',
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      setUser(firebaseUserToAppUser(fbUser));
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  const login = useCallback(async (email, password) => {
    const { user: fbUser } = await signInWithEmailAndPassword(auth, email, password);
    const token = await fbUser.getIdToken();
    if (token) localStorage.setItem('lms_token', token);
    setUser(firebaseUserToAppUser(fbUser));
    return { user: firebaseUserToAppUser(fbUser), token };
  }, []);

  const register = useCallback(async ({ name, email, password }) => {
    const { user: fbUser } = await createUserWithEmailAndPassword(auth, email, password);
    if (name && name.trim()) {
      await updateProfile(fbUser, { displayName: name.trim() });
    }
    const token = await fbUser.getIdToken();
    if (token) localStorage.setItem('lms_token', token);
    setUser(firebaseUserToAppUser({ ...fbUser, displayName: name?.trim() || fbUser.displayName }));
    return { user: firebaseUserToAppUser({ ...fbUser, displayName: name?.trim() || fbUser.displayName }), token };
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
    localStorage.removeItem('lms_token');
    localStorage.removeItem('lms_user');
    setUser(null);
  }, []);

  const changePassword = useCallback(async (oldPassword, newPassword) => {
    if (!auth.currentUser) throw new Error('Not authenticated');
    const credential = EmailAuthProvider.credential(auth.currentUser.email, oldPassword);
    await reauthenticateWithCredential(auth.currentUser, credential);
    await updatePassword(auth.currentUser, newPassword);
    return { detail: 'Password updated successfully.' };
  }, []);

  const value = { user, authReady, login, register, logout, changePassword, isAuthenticated: !!user };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
